import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CampaignContext, ProjectContext } from '../../contexts'
import Button from '../../ui/Button'
import Heading from '../../ui/Heading'
import { InfoTable } from '../../ui/InfoTable'
import Modal from '../../ui/Modal'
import { PreferencesContext } from '../../ui/PreferencesContext'
import { formatDate } from '../../utils'
import { CampaignForm } from './form/CampaignForm'
import { DeliveryRatio } from './ui/CampaignRates'
import { CampaignTag } from './ui/CampaignTag'
import ChannelTag from './ui/ChannelTag'
import { DelimitedJourneys, DelimitedLists } from './ui/DelimitedItems'
import { ModifiedTag } from './ui/ModifiedTag'
import { quietHoursLabel } from './ui/QuietHoursLabel'

export default function CampaignOverview() {
    const [project] = useContext(ProjectContext)
    const { t } = useTranslation()
    const [preferences] = useContext(PreferencesContext)
    const [campaign, setCampaign] = useContext(CampaignContext)
    const [isEditOpen, setIsEditOpen] = useState(false)

    const canEdit = campaign.type === 'trigger' || campaign.state === 'draft' || campaign.state === 'aborted'
    const isRunning = ['scheduled', 'running', 'loading'].includes(campaign.state)
    const hasJourneys = !!campaign.journeys?.length

    return (
        <>
            <Heading
                title={t('details')}
                size="h3"
                actions={
                    canEdit && <Button
                        size="small"
                        variant="secondary"
                        onClick={() => setIsEditOpen(true)}
                    >{t('edit_details')}</Button>
                }
            />

            <Heading title={t('channel')} size="h4" />
            <InfoTable rows={{
                [t('id')]: campaign.id,
                [t('channel')]: ChannelTag({ channel: campaign.channel }),
                [t('provider')]: campaign.provider?.name,
                [t('subscription_group')]: campaign.subscription?.name,
            }} />

            {campaign.type === 'blast' && <>
                <Heading title={t('delivery')} size="h4" />
                <InfoTable rows={{
                    [t('state')]: CampaignTag({ campaign }),
                    [t(isRunning ? 'scheduled_for' : 'launched_at')]: campaign.send_at ? formatDate(preferences, campaign.send_at, undefined, project.timezone) : undefined,
                    [t('in_timezone')]: campaign.send_in_user_timezone ? 'Yes' : 'No',
                    [t('send_lists')]: DelimitedLists({ lists: campaign.lists }),
                    [t('exclusion_lists')]: DelimitedLists({ lists: campaign.exclusion_lists }),
                    [t('delivery')]: DeliveryRatio({ delivery: campaign.delivery }),
                    ...campaign.settings?.quiet_hours && {
                        [t('quiet_hours')]: quietHoursLabel(campaign.settings.quiet_hours, t),
                    },
                }} />
            </>}

            <Heading title={t('timestamps')} size="h4" />
            <InfoTable rows={{
                ...campaign.updated_by && { [t('updated_at')]: <ModifiedTag admin={campaign.updated_by} /> },
                [t('created_at')]: campaign.created_at,
            }} />

            {
                (campaign.type === 'trigger' && hasJourneys)
                    && <>
                        <Heading title={t('associations')} size="h4" />
                        <InfoTable rows={{
                            [t('journeys')]: DelimitedJourneys({ journeys: campaign.journeys }),
                        }} />
                    </>
            }
            <Modal
                open={isEditOpen}
                onClose={setIsEditOpen}
                title={t('edit_campaign')}
                size="large"
            >
                <CampaignForm
                    campaign={campaign}
                    type={campaign.type}
                    onSave={campaign => {
                        setCampaign(campaign)
                        setIsEditOpen(false)
                    }}
                />
            </Modal>
        </>
    )
}
